/**
 * Chinese Classical basic-points table. Adapted from the classical "basic
 * points + doubles" Chinese Mah-Jongg scoring system as documented at
 * sloperama.com's Chinese Mah-Jongg rules page, cross-checked against the
 * standard doubling convention: concealed = 2× exposed, kong = 4× the
 * equivalent pung.
 *
 * Unlike the `double`/`limit` pattern detectors, base points are always at
 * least partially present (the winning bonus alone) — so this returns an
 * array of every contributing entry, not a single match-or-not check.
 */

import { SETS_TO_WIN, type Honor, type TileKind } from '../../game/types.js';
import { decomposeConcealedHand } from '../decompose.js';
import type { MatchedPattern, ScoringContext } from '../types.js';

const WINNING_BONUS = 20;
const SELF_DRAWN_BONUS = 2;

const PUNG_SIMPLE_EXPOSED = 2;
const PUNG_SIMPLE_CONCEALED = 4;
const PUNG_TERMINAL_EXPOSED = 4;
const PUNG_TERMINAL_CONCEALED = 8;
const KONG_SIMPLE_EXPOSED = 8;
const KONG_SIMPLE_CONCEALED = 16;
const KONG_TERMINAL_EXPOSED = 16;
const KONG_TERMINAL_CONCEALED = 32;

const PAIR_BONUS = 2;

const SIMPLE_RANK_MIN = 2;
const SIMPLE_RANK_MAX = 8;

const DRAGON_HONORS: readonly Honor[] = ['white', 'green', 'red'];

function isSimpleKind(kind: TileKind): boolean {
  return kind.kind === 'suit' && kind.rank >= SIMPLE_RANK_MIN && kind.rank <= SIMPLE_RANK_MAX;
}

function meldPoints(kind: TileKind, isKong: boolean, concealed: boolean): number {
  const simple = isSimpleKind(kind);
  if (isKong) {
    if (simple) return concealed ? KONG_SIMPLE_CONCEALED : KONG_SIMPLE_EXPOSED;
    return concealed ? KONG_TERMINAL_CONCEALED : KONG_TERMINAL_EXPOSED;
  }
  if (simple) return concealed ? PUNG_SIMPLE_CONCEALED : PUNG_SIMPLE_EXPOSED;
  return concealed ? PUNG_TERMINAL_CONCEALED : PUNG_TERMINAL_EXPOSED;
}

function pairBonusPatterns(pair: TileKind, context: ScoringContext): MatchedPattern[] {
  if (pair.kind !== 'honor') return [];
  const patterns: MatchedPattern[] = [];
  if (DRAGON_HONORS.includes(pair.honor)) {
    patterns.push({ name: 'pairBonus', kind: 'base', value: PAIR_BONUS });
  }
  if (pair.honor === context.seatWind) {
    patterns.push({ name: 'pairBonus', kind: 'base', value: PAIR_BONUS });
  }
  if (pair.honor === context.roundWind) {
    patterns.push({ name: 'pairBonus', kind: 'base', value: PAIR_BONUS });
  }
  return patterns;
}

/**
 * The always-present base-point breakdown: winning bonus, self-drawn bonus,
 * per-meld points (exposed melds via `Meld.concealed`, the remaining
 * concealed tiles via `decomposeConcealedHand`), and stackable pair bonuses.
 */
export function computeBasePoints(context: ScoringContext): MatchedPattern[] {
  const patterns: MatchedPattern[] = [{ name: 'wonHand', kind: 'base', value: WINNING_BONUS }];
  if (context.selfDrawn) {
    patterns.push({ name: 'selfDrawn', kind: 'base', value: SELF_DRAWN_BONUS });
  }

  for (const meldEntry of context.melds) {
    if (meldEntry.type === 'chi') continue;
    const first = meldEntry.tiles[0];
    if (first === undefined) continue;
    patterns.push({
      name: 'meldPoints',
      kind: 'base',
      value: meldPoints(first, meldEntry.type === 'kong', meldEntry.concealed),
    });
  }

  const setsNeeded = SETS_TO_WIN - context.melds.length;
  const decomposition = decomposeConcealedHand([...context.concealedTiles, context.winningTile], setsNeeded);
  if (decomposition !== null) {
    for (const set of decomposition.sets) {
      if (set.type === 'pung') {
        patterns.push({ name: 'meldPoints', kind: 'base', value: meldPoints(set.kind, false, true) });
      }
    }
    patterns.push(...pairBonusPatterns(decomposition.pair, context));
  }

  return patterns;
}
